import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import SafariExperiencesCarousel from '../../components/SafariExperiencesCarousel'
import FAQSection from '../../components/FAQSection'
import { safariExperiences } from '../../data/safariExperiences'

export default function WildlifeSafari12Days() {
    const [isModalOpen, setIsModalOpen] = useState(false)

    const faqs = [
        {
            question: "How physically demanding is this safari?",
            answer: "Moderate fitness is recommended. Gorilla trekking is the most strenuous activity (2-8 hours over challenging terrain), but rangers assign groups based on fitness levels. Chimpanzee tracking is typically easier (2-3 hours). The walking safari in Lake Mburo is gentle. Most time is spent in comfortable safari vehicles. Hiring porters for gorilla trekking helps significantly."
        },
        {
            question: "Can I do this safari with my family?",
            answer: "Most activities are family-friendly! However, gorilla trekking requires participants to be 15+ years old, and chimpanzee tracking is usually 12+. For families with younger children, we can arrange alternative activities like nature walks, cultural visits, or lodge-based relaxation during those days."
        },
        {
            question: "When should I book my permits?",
            answer: "Book 3-6 months in advance, especially for peak season travel (June-September, December-February). Gorilla permits are limited to protect the animals and sell out quickly. We'll secure your permits as soon as you confirm your travel dates. Wet season months (March-May, October-November) offer more availability."
        },
        {
            question: "What if I want to extend or shorten this itinerary?",
            answer: "Absolutely! We can easily customize the duration. Want to add Kidepo Valley for remote wilderness? Prefer to skip Lake Bunyonyi for a more focused wildlife experience? Need to condense it into 10 days? We'll adjust the itinerary to fit your available time and budget while maintaining quality experiences."
        }
    ]

    const itinerary = [
        {
            day: 1,
            title: "Arrival in Entebbe & Transfer",
            description: "Upon arrival at Entebbe International Airport, you will meet your safari guide and we'll transfer you to your accommodation. Depending on your arrival time, we might visit Mabamba Wetland where you will have the opportunity for shoebill stork viewing, or we can explore the Entebbe Botanical Gardens together.",
            highlights: ["Airport pickup", "Hotel check-in", "Optional Mabamba Wetland visit"]
        },
        {
            day: 2,
            title: "Transfer to Murchison Falls via Ziwa Rhino Sanctuary",
            description: "We'll begin your journey north with a stop at Ziwa Rhino Sanctuary for on-foot rhino tracking—your chance to see Uganda's rhinos in their natural habitat. We'll continue to Murchison Falls National Park, arriving in time for dinner. You will check into your lodge for relaxation.",
            highlights: ["Ziwa rhino tracking", "Scenic countryside drive", "Arrival at Murchison Falls"]
        },
        {
            day: 3,
            title: "Murchison Falls Game Drive & Nile Boat Cruise",
            description: "We'll start with an early morning game drive north of the Nile, searching together for lions, elephants, giraffes, leopards, and various antelope species. You will return for lunch. In the afternoon, we'll embark on a thrilling boat cruise to the base of Murchison Falls where you will observe hippos, crocodiles, and abundant birdlife including the rare shoebill stork. If you wish, we can arrange an optional hike to the top of the falls for spectacular views.",
            highlights: ["Morning game drive", "Boat cruise to Murchison Falls", "Optional hike to top of falls"]
        },
        {
            day: 4,
            title: "Transfer to Kibale Forest National Park",
            description: "We'll depart Murchison Falls and journey south through Uganda's scenic countryside to Kibale Forest National Park, the 'Primate Capital of the World.' The drive offers beautiful views of the Albertine Escarpment and rural homesteads. You will arrive at your lodge for dinner.",
            highlights: ["Scenic drive", "Albertine Escarpment views", "Arrive at Kibale"]
        },
        {
            day: 5,
            title: "Chimpanzee Tracking & Bigodi Wetland Walk",
            description: "After an early briefing, you will trek into Kibale Forest to find habituated chimpanzees. You will spend an hour watching these intelligent primates feed, play, and interact in their natural environment. In the afternoon, we'll explore Bigodi Wetland Sanctuary on a guided walk where you will spot diverse bird species and primates.",
            highlights: ["Chimpanzee tracking", "Bigodi Wetland walk", "Primate and bird sightings"]
        },
        {
            day: 6,
            title: "Transfer to Queen Elizabeth & Evening Game Drive",
            description: "We'll travel to Queen Elizabeth National Park, Uganda's most biodiverse protected area. Upon arrival, you will check in before we embark on an evening game drive through the park's varied landscapes—savannah, wetlands, and forests—searching for elephants, buffaloes, and antelopes as they become more active in the cooler hours.",
            highlights: ["Transfer to Queen Elizabeth", "Evening game drive", "Diverse ecosystems"]
        },
        {
            day: 7,
            title: "Queen Elizabeth Game Drive & Kazinga Channel Boat Safari",
            description: "We'll begin with an early morning game drive along the Kasenyi plains, prime lion territory, also searching for leopards, elephants, and Uganda kobs. You will return for lunch. In the afternoon, we'll enjoy a memorable boat cruise on the Kazinga Channel connecting Lakes Edward and George—famous for its highest concentration of hippos in Africa, plus crocodiles and spectacular birdlife that you will observe.",
            highlights: ["Morning game drive", "Kazinga Channel boat cruise", "Hippo and bird paradise"]
        },
        {
            day: 8,
            title: "Ishasha Tree-Climbing Lions & Transfer to Bwindi",
            description: "We'll drive through the Ishasha sector, renowned for its unique tree-climbing lions that lounge in fig trees—a behavior found in only two places worldwide. You will watch for these magnificent predators. We'll continue to Bwindi Impenetrable National Park, home to nearly half the world's mountain gorillas. You will enjoy the scenic drive that showcases breathtaking highland landscapes.",
            highlights: ["Tree-climbing lions", "Scenic highland drive", "Arrive at Bwindi"]
        },
        {
            day: 9,
            title: "Gorilla Trekking in Bwindi Impenetrable Forest",
            description: "The highlight of your safari! After a comprehensive briefing, you will trek into the ancient rainforest with experienced rangers to find a habituated gorilla family. You will spend one magical hour observing these gentle giants as they feed, play, and interact. The trek can last 2-8 hours depending on the gorillas' location. In the afternoon, if you wish, we can arrange an optional visit to the Batwa community where you will learn about their traditional forest lifestyle.",
            highlights: ["Gorilla trekking experience", "1 hour with gorilla family", "Optional Batwa cultural visit"]
        },
        {
            day: 10,
            title: "Transfer to Lake Bunyonyi",
            description: "We'll journey to the stunning Lake Bunyonyi, Africa's second-deepest lake, dotted with 29 picturesque islands. You will spend your afternoon canoeing on the tranquil waters, island hopping, or simply relaxing and reflecting on your gorilla encounter amidst breathtaking scenery.",
            highlights: ["Transfer to Lake Bunyonyi", "Canoeing", "Island exploration"]
        },
        {
            day: 11,
            title: "Lake Bunyonyi to Lake Mburo National Park",
            description: "We'll travel to Lake Mburo National Park, Uganda's smallest savannah park but rich in wildlife diversity. The journey takes you through terraced hills and pastoral landscapes with long-horned Ankole cattle. You will arrive in time for an evening boat trip on Lake Mburo or a game drive.",
            highlights: ["Scenic drive", "Ankole cattle country", "Evening boat trip or game drive"]
        },
        {
            day: 12,
            title: "Walking Safari, Game Drive & Return to Entebbe",
            description: "We'll start with a walking safari—Lake Mburo is one of the few parks where this is permitted. You will trek on foot with an armed ranger, getting close to zebras, impalas, elands, and antelopes. After breakfast, we'll enjoy a final game drive before beginning your return journey to Entebbe. We'll stop at the Equator monument where you will have time for photos and souvenir shopping. You will arrive in Entebbe for your departure flight or overnight stay.",
            highlights: ["Walking safari", "Final game drive", "Equator monument stop"]
        }
    ]

    return (
        <div>
            {/* Hero Section */}
            <section
                className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
                style={{
                    backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(/images/packages/12-days-safari.jpg)',
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl text-white">
                        <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
                            12 Days Uganda Wildlife Safari Tour – 2025/2026
                        </h1>
                        <p className="text-xl md:text-2xl mb-6">
                            Primates, predators, and Uganda's wildest landscapes
                        </p>
                        <div className="flex items-center gap-6 mb-6">
                            <div className="flex items-center gap-2">
                                <div className="flex text-safari-gold">
                                    {[...Array(5)].map((_, i) => (
                                        <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                                    ))}
                                </div>
                                <span className="text-lg">(320+ reviews)</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mb-8">
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                                UWA Licensed
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-leaf" className="w-4 h-4 mr-2" />
                                Carbon Neutral
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-heart" className="w-4 h-4 mr-2" />
                                Conservation Focused
                            </div>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            Request Customized Itinerary
                        </button>
                    </div>
                </div>
            </section>

            {/* Page Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="lg:grid lg:grid-cols-3 lg:gap-12">
                    {/* Main Content */}
                    <main className="lg:col-span-2">
                        {/* Full Safari Description */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                                Your 12-Day Wildlife Adventure Awaits
                            </h2>

                            <div className="prose prose-lg max-w-none">
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    Twelve days is the sweet spot for experiencing Uganda's incredible wildlife diversity without feeling rushed. This safari strikes the perfect balance between primate encounters and classic Big Five game viewing, taking you through four of Uganda's most spectacular national parks: Murchison Falls, Kibale Forest, Queen Elizabeth, and Bwindi Impenetrable.
                                </p>

                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    You'll start by tracking white rhinos on foot at Ziwa Sanctuary before heading to Murchison Falls, where the Nile River explodes through a narrow gorge with thunderous force. From there, journey to Kibale Forest to trek with chimpanzees—our closest living relatives after bonobos.  In Queen Elizabeth National Park, you'll witness the famous tree-climbing lions of Ishasha and cruise past hundreds of hippos on the Kazinga Channel. The grand finale? One unforgettable hour with mountain gorillas in Bwindi's misty rainforest.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">What Makes This Safari Special</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    This 12-day itinerary is designed for wildlife enthusiasts who want comprehensive coverage of Uganda's biodiversity without the extensive time commitment of longer safaris. You're not just checking boxes—each park offers genuinely different ecosystems and species. Murchison's open savannah couldn't be more different from Bwindi's dense rainforest, yet both deliver spectacular wildlife encounters.
                                </p>

                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    The safari balances active experiences (gorilla and chimp trekking, walking safari) with relaxing boat cruises and game drives. You'll have time to absorb each destination without exhausting yourself. Lake Bunyonyi offers restful reflection mid-safari, letting you process the incredible gorilla encounter. The pacing feels natural, not forced.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Best Time to Visit</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    The dry seasons (June-September and December-February) offer optimal conditions for this safari. Trails are more accessible for gorilla trekking, wildlife viewing is excellent as animals gather at water sources, and road conditions between parks are better. However, Uganda's equatorial climate means safaris are possible year-round, with the wet season bringing lush vegetation and excellent birding.
                                </p>
                            </div>
                        </section>

                        {/* Safari Highlights */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                                Safari Highlights
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-paw" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Mountain Gorilla Encounter</h3>
                                        <p className="text-gray-700">One magical hour with endangered mountain gorillas in Bwindi's ancient forest</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-tree" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Chimpanzee Tracking</h3>
                                        <p className="text-gray-700">Watch playful chimpanzees in Kibale, the Primate Capital of the World</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-water" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Murchison Falls & Boat Safari</h3>
                                        <p className="text-gray-700">Witness the Nile's raw power and cruise past hippos, crocodiles, and shoebills</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-lion" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Tree-Climbing Lions</h3>
                                        <p className="text-gray-700">Spot the famous lions of Ishasha lounging in fig trees</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-ship" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Kazinga Channel Cruise</h3>
                                        <p className="text-gray-700">Africa's highest concentration of hippos plus incredible birdlife</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-person-walking" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Walking Safari</h3>
                                        <p className="text-gray-700">Experience intimate wildlife encounters on foot in Lake Mburo</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Day-by-Day Itinerary */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                                Your 12-Day Itinerary
                            </h2>
                            <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                                <p className="text-gray-700 text-lg">
                                    <strong>📋 Tentative Itinerary:</strong> This itinerary is flexible and can be adjusted based on your travel preferences, budget, and specific interests. We customize every safari to match your unique needs!
                                </p>
                            </div>

                            <div className="space-y-6">
                                {itinerary.map((day) => (
                                    <div key={day.day} className="card">
                                        <div className="p-6">
                                            <div className="flex gap-4">
                                                <div className="flex-shrink-0">
                                                    <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                                                        {day.day}
                                                    </div>
                                                </div>
                                                <div className="flex-grow">
                                                    <h3 className="text-xl font-bold text-safari-green mb-2">{day.title}</h3>
                                                    <p className="text-gray-700 mb-4">{day.description}</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {day.highlights.map((highlight, index) => (
                                                            <span
                                                                key={index}
                                                                className="bg-safari-light text-safari-green px-3 py-1 rounded-full text-sm"
                                                            >
                                                                {highlight}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* What's Included/Excluded */}
                        <section className="mb-16">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="card p-6">
                                    <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                                        What's Typically Included
                                    </h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">11 nights accommodation (lodges/camps)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">4x4 safari vehicle with pop-up roof</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Professional English-speaking driver-guide</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Gorilla trekking permit ($800)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Chimpanzee tracking permit ($250)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">All park entry fees</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Boat cruises and game drives as specified</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Full board meals (breakfast, lunch, dinner)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Bottled water during safari</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="card p-6">
                                    <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                                        What's Not Included
                                    </h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">International flights</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Uganda visa ($50)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Travel insurance</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Tips for guides and lodge staff</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Personal expenses and souvenirs</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Alcoholic beverages</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Porter hire for trekking (optional)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Optional activities not mentioned</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* FAQs */}
                        <FAQSection faqs={faqs} title="Common Questions" />

                        {/* Related Safari Experiences Carousel */}
                        <section>
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-4 text-center">
                                Explore More Safari Experiences
                            </h2>
                            <p className="text-center text-gray-700 mb-12 text-lg">
                                Discover other carefully curated safari adventures across Uganda
                            </p>

                            <SafariExperiencesCarousel
                                experiences={safariExperiences}
                                currentExperience="12-days-wildlife-safari"
                            />
                        </section>
                    </main>

                    {/* Sidebar */}
                    <aside className="lg:col-span-1">
                        <div className="sticky top-24">
                            <div className="card p-6 mb-8">
                                <h3 className="text-2xl font-bold text-safari-green mb-4">Safari Details</h3>
                                <div className="space-y-4 text-gray-700">
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Duration:</strong> 12 Days / 11 Nights</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Best Time:</strong> Jun-Sep, Dec-Feb</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-map-marked-alt" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Parks Visited:</strong> 5 Key Parks</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Difficulty:</strong> Moderate</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Group Size:</strong> Private or small group</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                                >
                                    Get Custom Quote
                                </button>

                                <div className="bg-safari-green/5 border border-safari-green/20 rounded-lg p-4 mt-4">
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        <span className="font-bold text-safari-green">🌿 Travel with Purpose:</span> Part of your booking contributes to our <Link to="/planting-green-paths" className="text-safari-green font-semibold hover:underline">'Planting Green Paths'</Link> tree planting initiative, helping restore Uganda's ecosystems by securing a tree, planting and tree growth follow up. You can choose to participate in planting a tree activity during your safari or simply support the cause.
                                    </p>
                                </div>
                            </div>

                            <div className="card p-6 mb-8">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-lightbulb" className="w-6 h-6 text-safari-gold" />
                                    Essential Packing
                                </h3>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Waterproof hiking boots</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Long pants and sleeves (neutral colors)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Rain jacket</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Gardening gloves</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Binoculars and camera</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Sunscreen and insect repellent</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="card p-6">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                                    Health Requirements
                                </h3>
                                <ul className="space-y-3 text-gray-700 text-sm">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Yellow fever vaccination mandatory</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Malaria prophylaxis recommended</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Travel insurance advised</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <BookingModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                experience="12 Days Uganda Wildlife Safari Tour"
            />
        </div>
    )
}
